import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { 
  TrendingUp, 
  TrendingDown, 
  PieChart, 
  Calculator, 
  Search, 
  ArrowRight,
  Wallet,
  Target,
  IndianRupee,
  BarChart3,
  Star,
  RefreshCw,
  Clock
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell } from "recharts";

export const DashboardPage = () => {
  const { user, token } = useAuth();
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [topFunds, setTopFunds] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [portfolioRes, fundsRes] = await Promise.all([
        axios.get(`${API}/portfolio/live-valuation`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }).catch(() => ({ data: { holdings: [], total_invested: 0, current_value: 0, total_gain: 0, gain_percentage: 0 } })),
        axios.get(`${API}/funds/top-performers`).catch(() => ({ data: { funds: [] } }))
      ]);
      setPortfolioData(portfolioRes.data);
      setTopFunds(fundsRes.data.funds || []);
      if (portfolioRes.data.last_updated) {
        setLastUpdated(new Date(portfolioRes.data.last_updated));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const refreshValuation = async () => {
    setRefreshing(true);
    await fetchData();
  };

  // Use live data or defaults
  const totalInvested = portfolioData?.total_invested || 250000;
  const currentValue = portfolioData?.current_value || 287500;
  const totalGain = portfolioData?.total_gain || 37500;
  const gainPercent = portfolioData?.gain_percentage || 15;
  const holdings = portfolioData?.holdings || [];

  // Sample chart data
  const chartData = [
    { month: "Jan", value: totalInvested * 0.8 },
    { month: "Feb", value: totalInvested * 0.85 },
    { month: "Mar", value: totalInvested * 0.82 },
    { month: "Apr", value: totalInvested * 0.95 },
    { month: "May", value: totalInvested },
    { month: "Jun", value: currentValue },
  ];

  const pieData = [
    { name: "Equity", value: 60, color: "hsl(var(--chart-1))" },
    { name: "Debt", value: 25, color: "hsl(var(--chart-2))" },
    { name: "Hybrid", value: 15, color: "hsl(var(--chart-3))" },
  ];

  const quickActions = [
    { icon: Calculator, label: "SIP Calculator", to: "/calculators", color: "bg-amber-500/10 text-amber-500" },
    { icon: Search, label: "Research Funds", to: "/research", color: "bg-blue-500/10 text-blue-500" },
    { icon: Star, label: "My Watchlist", to: "/watchlist", color: "bg-purple-500/10 text-purple-500" },
    { icon: PieChart, label: "My Portfolio", to: "/portfolio", color: "bg-green-500/10 text-green-500" },
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-content">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold mb-1">
              Welcome back, <span className="gold-text">{user?.name?.split(' ')[0]}</span>
            </h1>
            <div className="flex items-center gap-3">
              <p className="text-muted-foreground">Here's your investment overview</p>
              {lastUpdated && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Updated {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-white/10"
              onClick={refreshValuation}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Link to="/research">
              <Button className="btn-primary" data-testid="invest-now-btn">
                <IndianRupee className="w-4 h-4 mr-2" />
                Invest Now
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="glass-card" data-testid="total-invested-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-primary" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Total Invested</p>
              <p className="font-mono text-2xl font-bold">{formatCurrency(totalInvested || 250000)}</p>
            </CardContent>
          </Card>

          <Card className="glass-card" data-testid="current-value-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Current Value</p>
              <p className="font-mono text-2xl font-bold">{formatCurrency(currentValue || 287500)}</p>
            </CardContent>
          </Card>

          <Card className="glass-card" data-testid="total-gain-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Total Gain</p>
              <p className="font-mono text-2xl font-bold text-green-500">
                +{formatCurrency(totalGain || 37500)}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card" data-testid="returns-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Returns</p>
              <p className="font-mono text-2xl font-bold text-green-500">+{gainPercent || 15}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Portfolio Growth Chart */}
          <Card className="glass-card lg:col-span-2" data-testid="growth-chart">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Portfolio Growth</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                    <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `₹${v/1000}K`} />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [formatCurrency(value), 'Value']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="hsl(var(--chart-1))" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorValue)"
                      className="chart-glow"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Asset Allocation */}
          <Card className="glass-card" data-testid="allocation-chart">
            <CardHeader>
              <CardTitle className="font-serif text-lg">Asset Allocation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {pieData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: item.color }}></div>
                    <span className="text-sm text-muted-foreground">{item.name} ({item.value}%)</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="font-serif text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <Link key={action.to} to={action.to}>
                  <Card className="glass-card card-hover cursor-pointer" data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <CardContent className="p-6 flex flex-col items-center text-center">
                      <div className={`w-14 h-14 rounded-xl ${action.color} flex items-center justify-center mb-3`}>
                        <Icon className="w-7 h-7" />
                      </div>
                      <p className="font-medium text-sm">{action.label}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Top Performing Funds */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl font-semibold">Top Performing Funds</h2>
            <Link to="/research" className="text-primary text-sm hover:underline flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(topFunds.length > 0 ? topFunds.slice(0, 6) : [
              { schemeCode: "119551", schemeName: "Aditya Birla Sun Life Frontline Equity Fund", returns1Y: 24.5, category: "Equity" },
              { schemeCode: "120503", schemeName: "HDFC Mid-Cap Opportunities Fund", returns1Y: 32.1, category: "Equity" },
              { schemeCode: "118989", schemeName: "Axis Bluechip Fund", returns1Y: 20.3, category: "Equity" },
            ]).map((fund) => (
              <Link key={fund.schemeCode} to={`/research/${fund.schemeCode}`}>
                <Card className="glass-card card-hover" data-testid={`top-fund-${fund.schemeCode}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium text-sm line-clamp-2 flex-1">{fund.schemeName}</p>
                      <div className="flex items-center gap-1 text-green-500 ml-2 shrink-0">
                        <TrendingUp className="w-4 h-4" />
                        <span className="font-mono text-sm">+{fund.returns1Y}%</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{fund.category}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
